import React from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';

import UnityHandler from '../../designer/UnityHandler';
import {
    VIEW_OPTIONS, ViewOption, VIEW_ACTIONS, ViewAction, readViewOption, writeViewOption,
} from '../unityView/viewOptions';
import {
    UNITY_EDITOR_URL, isUnityEditorModeEnabled, setUnityEditorMode, probeUnityEditor,
} from '../unityView/transport/unityEditorMode';

/**
 * Nézet tab (admin only): dev/preview toggles that change the Unity viewer's
 * appearance. Each switch calls a method on the 'WebInterface' game object with
 * a "true"/"false" string, exactly like the toolbar actions (SetDoorsOpen etc.)
 * and the script tabs (SetUConScript/SetStyleScript) do via UnityHandler.
 *
 * State is persisted in sessionStorage (see viewOptions.ts): it survives a page
 * reload — so an admin testing the gizmos doesn't have to re-flip them every
 * refresh — but is cleared on browser restart. UnityView re-applies the stored
 * state to the viewer once the model has loaded, so the initial state shown here
 * matches what the viewer is actually rendering.
 */
const ViewOptionsTab: React.FC = () => {
    const [state, setState] = React.useState<Record<string, boolean>>(() =>
        VIEW_OPTIONS.reduce<Record<string, boolean>>((acc, t) => { acc[t.id] = readViewOption(t.method); return acc; }, {})
    );

    const handleToggle = (toggle: ViewOption) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const enabled = e.target.checked;
        setState((prev) => ({ ...prev, [toggle.id]: enabled }));
        writeViewOption(toggle.method, enabled);
        UnityHandler.instance?.SendMessage('WebInterface', toggle.method, enabled ? 'true' : 'false');
    };

    // Fire-and-forget: no local state, nothing written to sessionStorage, nothing replayed on
    // reload. The viewer owns the camera from here on — pressing it again just re-issues it.
    const handleAction = (action: ViewAction) => () => {
        UnityHandler.instance?.SendMessage('WebInterface', action.method, action.arg);
    };

    // ===== Unity editor mode =====
    // A kapcsoló ÚJRATÖLTI az oldalt, mert a mód mount-idejű döntés: bridge módban a WebGL build be
    // sem töltődik (nincs wasm-letöltés, nincs canvas). Lásd transport/unityEditorMode.ts.
    const [editorMode, setEditorMode] = React.useState<boolean>(isUnityEditorModeEnabled);
    const [probeFailed, setProbeFailed] = React.useState(false);

    const handleEditorMode = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.checked) {
            setUnityEditorMode(false);            // újratölt
            return;
        }
        // Bekapcsolás ELŐTT megnézzük, fut-e a híd. Enélkül újratöltenénk az oldalt, és az eredmény
        // "sem canvas, sem Unity" lenne — a legrosszabb fajta néma hiba.
        setProbeFailed(false);
        if (!(await probeUnityEditor())) {
            setProbeFailed(true);
            setEditorMode(false);
            return;
        }
        setUnityEditorMode(true);                 // újratölt
    };

    return (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                A kapcsolók a 3D nézet megjelenését módosítják (a Unity nézőben). Csak admin módban.
                A beállítás az oldal újratöltését túléli, de a böngésző bezárásakor törlődik.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {VIEW_OPTIONS.map((toggle) => (
                    <FormControlLabel
                        key={toggle.id}
                        control={<Switch checked={state[toggle.id]} onChange={handleToggle(toggle)} />}
                        label={toggle.label}
                    />
                ))}
            </Box>

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Egyszeri kameraparancsok — nem kapcsolók, nem őrzik meg az állapotukat.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {VIEW_ACTIONS.map((action) => (
                    <Button
                        key={action.id}
                        variant="outlined"
                        size="small"
                        onClick={handleAction(action)}
                    >
                        {action.label}
                    </Button>
                ))}
            </Box>

            <Divider sx={{ my: 1.5 }} />

            <FormControlLabel
                control={<Switch checked={editorMode} onChange={handleEditorMode} />}
                label="Unity editor mode"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Bekapcsolva a 3D nem a beépített WebGL programmal beszél, hanem a gépeden futó Unity
                Editorral ({UNITY_EDITOR_URL}): a paraméterek oda mennek, a Unity hibái és logjai a
                böngésző konzoljára. Ilyenkor nincs canvas — a modell az Editor Game view-jában
                látszik. A kapcsoló újratölti az oldalt.
            </Typography>
            {probeFailed && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                    Nem válaszol semmi a {UNITY_EDITOR_URL}/health címen. Indítsd el a Unity Editort
                    Play módban, vagy a mock hidat (<code>node docs/unity-bridge/mock-bridge.js</code>),
                    aztán próbáld újra — az oldalt nem töltöttem újra.
                </Typography>
            )}
        </Box>
    );
};

export default ViewOptionsTab;
